import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const BASE = process.env.REACT_APP_API_URL || '/api';
const AVATAR_COLORS = ['#D4873A','#7B6FD0','#2A9E82','#C06090','#C8A84B','#5B9ED0','#C05050'];

function colorFor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function EntryAvatar({ entry, size = 32 }) {
  const [imgErr, setImgErr] = useState(false);
  const col = entry.color || colorFor(entry.name);
  if (entry.picture && !imgErr) {
    return (
      <img
        src={entry.picture}
        alt={entry.name}
        onError={() => setImgErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `1.5px solid ${col}55`, flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `${col}20`, border: `1.5px solid ${col}55`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: col, flexShrink: 0,
    }}>
      {entry.initials}
    </div>
  );
}

function ScoresTab({ lang, allUsers, onOpenProfile }) {
  const nl = lang === 'nl';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const medals = ['🥇', '🥈', '🥉'];

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE}/games/leaderboard`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const handleRowClick = (entry) => {
    if (!onOpenProfile) return;
    // Try to find full user object from allUsers first (has all fields)
    const found = (allUsers || []).find(u => u.id === entry.userId);
    if (found) { onOpenProfile(found); return; }
    // Fallback: build minimal user object from leaderboard data
    if (entry.userId) {
      onOpenProfile({ id: entry.userId, name: entry.name, picture: entry.picture || null, color: entry.color || colorFor(entry.name) });
    }
  };

  if (loading) return <TechLoadingBar lang={lang} />;

  if (error || data?.error) return (
    <div style={{ textAlign: 'center', padding: 40, color: C.silver, fontSize: 14 }}>
      {nl ? 'Kon scores niet laden.' : 'Could not load scores.'}
    </div>
  );

  const games = Object.entries(data || {});

  if (games.length === 0) return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>🏆</div>
      <div style={{ color: C.white, fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
        {nl ? 'Nog geen scores!' : 'No scores yet!'}
      </div>
      <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.6 }}>
        {nl
          ? 'Speel een spel om als eerste op het leaderboard te staan.'
          : 'Play a game to be the first on the leaderboard.'}
      </div>
    </div>
  );

  return (
    <div className="fadeUp">
      {games.map(([gameId, game]) => (
        <div key={gameId} style={s_GC({ marginBottom: 10 })}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 20 }}>{game.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{game.label}</div>
              <div style={{ fontSize: 10, color: C.silver, marginTop: 1 }}>
                {game.lowerBetter
                  ? (nl ? 'Laagste score wint' : 'Lowest score wins')
                  : (nl ? 'Hoogste score wint' : 'Highest score wins')}
              </div>
            </div>
          </div>
          {game.entries.map((e, i) => (
            <div key={i}
              onClick={() => handleRowClick(e)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 0',
                borderBottom: i < game.entries.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none',
                cursor: e.userId && onOpenProfile ? 'pointer' : 'default',
              }}>
              <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>
                {medals[i] || `#${i + 1}`}
              </span>
              <EntryAvatar entry={e} size={32} />
              <div style={{ flex: 1, color: C.white, fontSize: 13 }}>{e.name}</div>
              <div style={{
                color: i === 0 ? _T.accent : C.silver2,
                fontSize: 13, fontWeight: i === 0 ? 700 : 500,
              }}>
                {e.display}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default ScoresTab;
